import Promise from 'promise';
import {
  FETCH_APPLICANTS_DATA_FAILURE,
  FETCH_APPLICANTS_DATA_SUCCESS,
  PARENT_APP_INFO_RECEIVED,
  UPDATE_LISTING_RENT,
} from '../constants/actionTypes';
import { CANCEL_APPLICATION_MANAGER_IDV_REQUEST, REQUEST_APPLICATION_MANAGER_TO_IDV } from '../constants/actions';
import { fetchApplicantsDataApi, getListingDetailsApi, logFirstTimeViewingApi, processDataWrap } from '../utils/api';
import { applicationReviewLoadingStatusUpdate } from '../actions/loadingStatusActionCreators';
import {
  CANNOT_FIND_SNAPSHOT_ERROR,
  CANNOT_FIND_SNAPSHOT_MESSAGE,
  FETCH_DATA_ERROR_MSG,
  INIT_LANDLORD_REVIEW_ERROR,
} from '../constants/messages';
import { isBrownoutOn } from '../utils/brownoutUtil';
import { logError } from '@zg-rentals/log-error';

export function fetchApplicantsDataSuccess(payload) {
  return { type: FETCH_APPLICANTS_DATA_SUCCESS, payload };
}

function fetchApplicantsDataFailure(payload) {
  return { type: FETCH_APPLICANTS_DATA_FAILURE, payload };
}

function updateListingRent(payload) {
  return { type: UPDATE_LISTING_RENT, payload };
}

const logFirstTimeViewing =
  (payload = {}) =>
  (dispatch) => {
    dispatch(logFirstTimeViewingApi(payload)).then(
      (response = {}) => {
        const { data = {} } = response;
        if (data.status !== 'success') {
          logError({ error: data.message });
        }
      },
      (serverError) => {
        logError({
          context: "Failed to log Irene's first time viewing application.",
          error: serverError.message || serverError,
        });
      },
    );
  };

export const fetchListingData = (payload) => (dispatch) => {
  dispatch(getListingDetailsApi(payload)).then(
    (response = {}) => {
      if (response && response.data && response.data.response) {
        const { floorPlans = [] } = response.data.response;
        dispatch(
          updateListingRent({
            rentPrice: floorPlans[0] ? floorPlans[0].highPrice : 0,
          }),
        );
      } else {
        logError({ context: 'Failed to get listing details', error: response });
      }
    },
    (serverError) => {
      logError({ context: 'Failed to get listing details. Error: ', error: serverError.message || serverError });
    },
  );
};

function initApplicantsData(payload = {}) {
  return (dispatch) => {
    dispatch(
      applicationReviewLoadingStatusUpdate({
        showSpinner: true,
        show: true,
      }),
    );

    return dispatch(fetchApplicantsDataApi(payload)).then(
      (response = {}) => {
        try {
          if (response && response.data) {
            const currentApplication = processDataWrap(response.data);
            dispatch(fetchApplicantsDataSuccess({ currentApplication }));
            dispatch(
              applicationReviewLoadingStatusUpdate({
                showSpinner: false,
                show: false,
              }),
            );
            (currentApplication?.applicantsData || []).forEach(({ userId: applicantId, zgAuthZone, applicationId }) => {
              if (applicantId && applicationId && zgAuthZone) {
                dispatch(
                  logFirstTimeViewing({
                    applicantId,
                    zgAuthZone: zgAuthZone.toLowerCase(),
                    applicationId,
                  }),
                );
              }
            });
            return Promise.resolve({ currentApplication });
          } else {
            const errors = { fetchData: FETCH_DATA_ERROR_MSG };
            dispatch(fetchApplicantsDataFailure({ errors }));
            dispatch(
              applicationReviewLoadingStatusUpdate({
                message: INIT_LANDLORD_REVIEW_ERROR,
                isError: true,
                showSpinner: false,
                show: true,
              }),
            );
            return Promise.reject({ errors });
          }
        } catch (error) {
          logError({ error, context: 'init app' });
        }
      },
      (serverError) => {
        if (isBrownoutOn(serverError)) {
          return Promise.reject(serverError);
        }

        let errorMessage = INIT_LANDLORD_REVIEW_ERROR;

        try {
          const message = serverError?.response?.data?.error?.message;

          // Show specific error message, if needed.
          if (message === CANNOT_FIND_SNAPSHOT_MESSAGE) {
            errorMessage = CANNOT_FIND_SNAPSHOT_ERROR;
          }
        } finally {
          const errors = { fetchData: FETCH_DATA_ERROR_MSG };
          dispatch(fetchApplicantsDataFailure({ errors }));
          dispatch(
            applicationReviewLoadingStatusUpdate({
              message: errorMessage,
              isError: true,
              showSpinner: false,
              show: true,
            }),
          );
          return Promise.reject({ errors });
        }
      },
    );
  };
}

function refreshApplicantsData(payload = {}) {
  return (dispatch) => {
    return dispatch(fetchApplicantsDataApi(payload)).then(
      (response = {}) => {
        if (response && response.data) {
          const currentApplication = processDataWrap(response.data);
          dispatch(fetchApplicantsDataSuccess({ currentApplication }));
          return Promise.resolve({ currentApplication });
        } else {
          const errors = { fetchData: FETCH_DATA_ERROR_MSG };
          dispatch(fetchApplicantsDataFailure({ errors }));
          return Promise.reject({ errors });
        }
      },
      (serverError) => {
        if (isBrownoutOn(serverError)) {
          return Promise.reject(serverError);
        }
        const errors = { fetchData: FETCH_DATA_ERROR_MSG };
        dispatch(fetchApplicantsDataFailure({ errors }));
        return Promise.reject({ errors });
      },
    );
  };
}

export const loadApplicantsData = (payload = {}) => {
  return (dispatch, getState) => {
    const { rentalApplication = {} } = getState();
    const { loadingStatus = {} } = rentalApplication;
    const { initRequestStatus = {} } = loadingStatus;

    if (initRequestStatus.applicantsDataLoaded) {
      return dispatch(refreshApplicantsData(payload));
    } else {
      return dispatch(initApplicantsData(payload));
    }
  };
};

export function receiveParentAppInfo(app, user) {
  return {
    type: PARENT_APP_INFO_RECEIVED,
    app,
    user,
  };
}

export function requestApplicationManagerToIdv() {
  return {
    type: REQUEST_APPLICATION_MANAGER_TO_IDV,
  };
}

export function cancelApplicationManagerIdvRequest() {
  return {
    type: CANCEL_APPLICATION_MANAGER_IDV_REQUEST,
  };
}
